import { isDateFilterEmpty } from "@/components/date-filter/date-filter.utils.ts";
import {
  ApiDateFilter,
  ApiDateFilterMode,
  ApiDateFilterOperator,
  ApiDateFilterPreset,
} from "@/components/date-filter/date-filter.type.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiPartialDate } from "@/types/common.types.ts";
import {
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";
import { useTranslation } from "react-i18next";

const WEEK = { weekStartsOn: 1 } as const;

export const EXACT_PRESETS: ApiDateFilterPreset[] = [
  ApiDateFilterPreset.this_week,
  ApiDateFilterPreset.last_week,
  ApiDateFilterPreset.next_week,
  ApiDateFilterPreset.this_month,
  ApiDateFilterPreset.last_month,
  ApiDateFilterPreset.next_month,
  ApiDateFilterPreset.this_quarter,
  ApiDateFilterPreset.last_quarter,
  ApiDateFilterPreset.next_quarter,
  ApiDateFilterPreset.this_year,
  ApiDateFilterPreset.last_year,
  ApiDateFilterPreset.next_year,
  ApiDateFilterPreset.last_30_days,
  ApiDateFilterPreset.last_3_months,
];

export const ANNUAL_THIS_PRESETS: ApiDateFilterPreset[] = [
  ApiDateFilterPreset.this_week,
  ApiDateFilterPreset.this_month,
  ApiDateFilterPreset.this_quarter,
];

export const ANNUAL_FUTURE_PRESETS: ApiDateFilterPreset[] = [
  ApiDateFilterPreset.next_week,
  ApiDateFilterPreset.next_month,
  ApiDateFilterPreset.next_quarter,
];

export const ANNUAL_PAST_PRESETS: ApiDateFilterPreset[] = [
  ApiDateFilterPreset.last_week,
  ApiDateFilterPreset.last_month,
  ApiDateFilterPreset.last_quarter,
  ApiDateFilterPreset.last_30_days,
  ApiDateFilterPreset.last_3_months,
];

export interface PeriodPresetOption {
  preset: ApiDateFilterPreset;
  mode: ApiDateFilterMode;
}

export function buildPresetGroups(modes: ApiDateFilterMode[]): {
  exact: PeriodPresetOption[];
  annualThis: PeriodPresetOption[];
  annualFuture: PeriodPresetOption[];
  annualPast: PeriodPresetOption[];
} {
  const hasDay = modes.includes(ApiDateFilterMode.day);
  const toDayOptions = (presets: ApiDateFilterPreset[]) =>
    presets.map((preset) => ({ preset, mode: ApiDateFilterMode.day }));

  const exact = modes.includes(ApiDateFilterMode.date)
    ? EXACT_PRESETS.map((preset) => ({ preset, mode: ApiDateFilterMode.date }))
    : [];
  const annualThis = hasDay ? toDayOptions(ANNUAL_THIS_PRESETS) : [];
  const annualFuture = hasDay ? toDayOptions(ANNUAL_FUTURE_PRESETS) : [];
  const annualPast = hasDay ? toDayOptions(ANNUAL_PAST_PRESETS) : [];
  return { exact, annualThis, annualFuture, annualPast };
}

export function dateToPartialDate(d: Date, mode: ApiDateFilterMode): ApiPartialDate {
  return {
    year: mode === ApiDateFilterMode.day ? null : d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  };
}

export function withDisplayYear(pd: ApiPartialDate | null | undefined): ApiPartialDate | undefined {
  if (!pd) return undefined;
  return pd.year != null ? pd : { ...pd, year: new Date().getFullYear() };
}

function resolvePresetRange(preset: ApiDateFilterPreset, today: Date): { start: Date; end: Date } {
  switch (preset) {
    case ApiDateFilterPreset.this_week:
      return { start: startOfWeek(today, WEEK), end: endOfWeek(today, WEEK) };
    case ApiDateFilterPreset.last_week: {
      const d = subWeeks(today, 1);
      return { start: startOfWeek(d, WEEK), end: endOfWeek(d, WEEK) };
    }
    case ApiDateFilterPreset.this_month:
      return { start: startOfMonth(today), end: endOfMonth(today) };
    case ApiDateFilterPreset.last_month: {
      const d = subMonths(today, 1);
      return { start: startOfMonth(d), end: endOfMonth(d) };
    }
    case ApiDateFilterPreset.last_30_days:
      return { start: subDays(today, 29), end: today };
    case ApiDateFilterPreset.last_3_months:
      return { start: startOfMonth(subMonths(today, 2)), end: endOfMonth(today) };
    case ApiDateFilterPreset.this_quarter:
      return { start: startOfQuarter(today), end: endOfQuarter(today) };
    case ApiDateFilterPreset.last_quarter: {
      const d = subQuarters(today, 1);
      return { start: startOfQuarter(d), end: endOfQuarter(d) };
    }
    case ApiDateFilterPreset.this_year:
      return { start: startOfYear(today), end: endOfYear(today) };
    case ApiDateFilterPreset.last_year: {
      const d = subYears(today, 1);
      return { start: startOfYear(d), end: endOfYear(d) };
    }
    case ApiDateFilterPreset.next_week: {
      const d = addWeeks(today, 1);
      return { start: startOfWeek(d, WEEK), end: endOfWeek(d, WEEK) };
    }
    case ApiDateFilterPreset.next_month: {
      const d = addMonths(today, 1);
      return { start: startOfMonth(d), end: endOfMonth(d) };
    }
    case ApiDateFilterPreset.next_quarter: {
      const d = addQuarters(today, 1);
      return { start: startOfQuarter(d), end: endOfQuarter(d) };
    }
    case ApiDateFilterPreset.next_year: {
      const d = addYears(today, 1);
      return { start: startOfYear(d), end: endOfYear(d) };
    }
  }
}

function resolveRange(filter: ApiDateFilter): {
  from?: ApiPartialDate;
  to?: ApiPartialDate;
} {
  if (filter.operator === ApiDateFilterOperator.custom) {
    return { from: filter.value_from ?? undefined, to: filter.value_to ?? undefined };
  }
  if (!filter.preset) return {};
  const { start, end } = resolvePresetRange(filter.preset, new Date());
  return {
    from: dateToPartialDate(start, filter.mode),
    to: dateToPartialDate(end, filter.mode),
  };
}

export function useDateFilterFormat() {
  const { t } = useTranslation();
  const { renderPartialDate } = useFormatter();

  function formatRange(filter: ApiDateFilter | undefined): string | undefined {
    if (isDateFilterEmpty(filter)) return undefined;
    const f = filter as ApiDateFilter;
    const { from, to } = resolveRange(f);
    const fromStr = from ? renderPartialDate(from) : undefined;
    const toStr = to ? renderPartialDate(to) : undefined;

    let range: string | undefined;
    if (fromStr && toStr) {
      range = fromStr === toStr ? fromStr : `${fromStr} – ${toStr}`;
    } else if (fromStr) {
      range = `${t("common:date_from_short")} ${fromStr}`;
    } else if (toStr) {
      range = `${t("common:date_before")} ${toStr}`;
    }
    if (!range) return undefined;

    return f.mode === ApiDateFilterMode.day ? `${range}, ${t("common:any_year")}` : range;
  }

  function formatModeHint(mode: ApiDateFilterMode): string {
    return mode === ApiDateFilterMode.day
      ? t("common:period_hint_annual")
      : t("common:period_hint_exact");
  }

  return { formatRange, formatModeHint };
}
